import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import ParticleField from "./ParticleField";

// Scanline canvas background — matches the snake grid aesthetic
const GridBackground = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    const TILE = 36;
    let frame = 0;
    let raf;

    const draw = () => {
      raf = requestAnimationFrame(draw);
      frame++;
      const W = canvas.width;
      const H = canvas.height;

      ctx.fillStyle = "#000";
      ctx.fillRect(0, 0, W, H);

      // Subtle cyan grid
      ctx.strokeStyle = "rgba(0,245,255,0.045)";
      ctx.lineWidth = 0.5;
      const cols = Math.ceil(W / TILE);
      const rows = Math.ceil(H / TILE);
      for (let i = 0; i <= cols; i++) {
        ctx.beginPath(); ctx.moveTo(i * TILE, 0); ctx.lineTo(i * TILE, H); ctx.stroke();
      }
      for (let j = 0; j <= rows; j++) {
        ctx.beginPath(); ctx.moveTo(0, j * TILE); ctx.lineTo(W, j * TILE); ctx.stroke();
      }

      // Drifting glitch lines
      if (frame % 180 === 0) {
        ctx.fillStyle = `rgba(0,245,255,${Math.random() * 0.06})`;
        const y = Math.random() * H;
        ctx.fillRect(0, y, W, 1 + Math.random() * 2);
      }

      // Occasional random cell flicker
      if (frame % 8 === 0) {
        const rx = Math.floor(Math.random() * cols) * TILE;
        const ry = Math.floor(Math.random() * rows) * TILE;
        ctx.fillStyle = `rgba(0,255,136,${Math.random() * 0.07})`;
        ctx.fillRect(rx + 1, ry + 1, TILE - 2, TILE - 2);
      }
    };

    draw();
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", resize); };
  }, []);

  return <canvas ref={canvasRef} style={{ position: "absolute", inset: 0, display: "block" }} />;
};

const GAMES = [
  {
    id: "secret",
    label: "NEURAL OVERRIDE",
    sub: "Consume. Expand. Collapse.",
    color: "rgb(125, 78, 218)",
    glow: "rgba(162, 0, 255, 0.35)",
    icon: "⬡",
    accent: "#00ff88",
  },
  {
    id: "flappy",
    label: "FLAPPY BOX",
    sub: "One tap from oblivion.",
    color: "#ff0055",
    glow: "rgba(255,0,85,0.35)",
    icon: "◈",
    accent: "#ff6688",
  },
  {
    id: "snake",
    label: "SYNAPSE SNAKE",
    sub: "Navigate the neural grid.",
    color: "#00ff88",
    glow: "rgba(0,255,136,0.35)",
    icon: "▣",
    accent: "#00f5ff",
  },
];

const GameCard = ({ game, index, onClick }) => {
  const [hovered, setHovered] = useState(false);

  return (
    <motion.button
      onClick={() => onClick(game.id)}
      onHoverStart={() => setHovered(true)}
      onHoverEnd={() => setHovered(false)}
      initial={{ opacity: 0, x: -40 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: 0.3 + index * 0.12, duration: 0.5, ease: "easeOut" }}
      whileTap={{ scale: 0.97 }}
      style={{
        position: "relative",
        width: "100%",
        padding: "0",
        background: "transparent",
        border: "none",
        cursor: "pointer",
        outline: "none",
        textAlign: "left",
      }}
    >
      {/* Card body */}
      <motion.div
        animate={{
          borderColor: hovered ? game.color : "rgba(255,255,255,0.1)",
          boxShadow: hovered
            ? `0 0 24px ${game.glow}, inset 0 0 24px rgba(0,0,0,0.6)`
            : `0 0 0px transparent, inset 0 0 0px transparent`,
          background: hovered ? "rgba(0,0,0,0.85)" : "rgba(0,0,0,0.5)",
        }}
        transition={{ duration: 0.2 }}
        style={{
          border: "1px solid rgba(255,255,255,0.1)",
          padding: "20px 24px",
          display: "flex",
          alignItems: "center",
          gap: 20,
          backdropFilter: "blur(4px)",
        }}
      >
        {/* Index indicator */}
        <div style={{
          fontFamily: "monospace",
          fontSize: 10,
          color: "rgba(255,255,255,0.2)",
          letterSpacing: 2,
          minWidth: 24,
        }}>
          {String(index + 1).padStart(2, "0")}
        </div>

        {/* Icon cell */}
        <motion.div
          animate={{
            color: hovered ? game.color : "rgba(255,255,255,0.2)",
            textShadow: hovered ? `0 0 16px ${game.color}` : "none",
          }}
          style={{ fontSize: 22, lineHeight: 1, minWidth: 28, textAlign: "center" }}
        >
          {game.icon}
        </motion.div>

        {/* Text */}
        <div style={{ flex: 1 }}>
          <motion.div
            animate={{ color: hovered ? game.color : "#fff" }}
            style={{
              fontFamily: "monospace",
              fontSize: 13,
              letterSpacing: 4,
              fontWeight: 700,
              textShadow: hovered ? `0 0 12px ${game.color}` : "none",
              marginBottom: 4,
            }}
          >
            {game.label}
          </motion.div>
          <div style={{
            fontFamily: "monospace",
            fontSize: 10,
            color: "rgba(255,255,255,0.3)",
            letterSpacing: 2,
          }}>
            {game.sub}
          </div>
        </div>

        {/* Arrow */}
        <motion.div
          animate={{
            x: hovered ? 4 : 0,
            color: hovered ? game.color : "rgba(255,255,255,0.15)",
            opacity: hovered ? 1 : 0.4,
          }}
          style={{ fontFamily: "monospace", fontSize: 14 }}
        >
          ›
        </motion.div>
      </motion.div>

      {/* Left accent bar */}
      <motion.div
        animate={{ scaleY: hovered ? 1 : 0, opacity: hovered ? 1 : 0 }}
        initial={{ scaleY: 0, opacity: 0 }}
        transition={{ duration: 0.15 }}
        style={{
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: 2,
          background: game.color,
          boxShadow: `0 0 8px ${game.color}`,
          transformOrigin: "top",
        }}
      />
    </motion.button>
  );
};

export default function ArcadeMenu({ mode, setMode }) {
  // For standalone preview, manage mode internally if not passed as props
  const [localMode, setLocalMode] = useState("game");
  const currentMode = mode ?? localMode;
  const currentSetMode = setMode ?? setLocalMode;

  return (
    <AnimatePresence>
      {currentMode === "game" && (
        <motion.div
          key="arcade-menu"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 9999,
            background: "#000",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
          }}
        >
          <GridBackground />

        <div className="absolute inset-0 pointer-events-none">
          {/* <div className="absolute inset-0 bg-background dark:bg-background" /> */}
          {/* <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/5 dark:to-black/40" /> */}
        </div>

      {/* Radial glow effects */}
      <div
        className="absolute top-1/4 left-1/3 w-[600px] h-[600px] rounded-full pointer-events-none hidden dark:block"
        style={{
          background: 'radial-gradient(circle, hsl(var(--primary) / 0.07) 0%, transparent 70%)',
        }}
      />
      <div
        className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] rounded-full pointer-events-none hidden dark:block"
        style={{
          background: 'radial-gradient(circle, hsl(var(--secondary) / 0.05) 0%, transparent 70%)',
        }}
      />

      <div
        className="absolute right-[15%] top-1/2 w-[450px] h-[450px] rounded-full pointer-events-none block dark:hidden"
        style={{
          background: 'radial-gradient(circle, hsl(var(--primary) / 0.08) 0%, transparent 65%)',
          transform: 'translateY(-50%)'
        }}
      />

      {/* Grid dots */}
      <div className="absolute inset-0 bg-grid pointer-events-none dark:opacity-40 opacity-15" />

      {/* Particles */}
      <div className="absolute inset-0">
        <ParticleField />
      </div>
          {/* Content */}
          <div style={{ position: "relative", zIndex: 1, width: "100%", maxWidth: 420, padding: "0 24px" }}>

            {/* Header */}
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              style={{ textAlign: "center", marginBottom: 48 }}
            >
              {/* Glitch title */}
              <div style={{ position: "relative", display: "inline-block" }}>
                <motion.h1
                  style={{
                    fontFamily: "monospace",
                    fontSize: 11,
                    letterSpacing: 10,
                    color: "#00f5ff",
                    opacity: 0.5,
                    marginBottom: 10,
                    textShadow: "0 0 10px #00f5ff",
                  }}
                >
                  SYSTEM / ARCADE
                </motion.h1>
                <motion.div
                  style={{
                    fontFamily: "monospace",
                    fontSize: 36,
                    letterSpacing: 12,
                    color: "#fff",
                    textShadow: "0 0 30px rgba(0,245,255,0.4)",
                    fontWeight: 900,
                  }}
                >
                  ARCADE
                </motion.div>
                {/* Underline */}
                <motion.div
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ delay: 0.5, duration: 0.6 }}
                  style={{
                    height: 1,
                    background: "linear-gradient(90deg, transparent, #00f5ff, transparent)",
                    marginTop: 12,
                    transformOrigin: "center",
                  }}
                />
              </div>

              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.8 }}
                style={{
                  fontFamily: "monospace",
                  fontSize: 10,
                  letterSpacing: 3,
                  color: "rgba(255,255,255,0.25)",
                  marginTop: 12,
                }}
              >
                SELECT SIMULATION
              </motion.p>
            </motion.div>

            {/* Game list */}
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {GAMES.map((game, i) => (
                <GameCard
                  key={game.id}
                  game={game}
                  index={i}
                  onClick={(id) => currentSetMode(id)}
                />
              ))}
            </div>

            {/* Footer / back */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.9 }}
              style={{ textAlign: "center", marginTop: 40 }}
            >
              <button
                onClick={() => currentSetMode("portfolio")}
                style={{
                  background: "transparent",
                  border: "none",
                  fontFamily: "monospace",
                  fontSize: 10,
                  letterSpacing: 4,
                  color: "rgba(255,255,255,0.25)",
                  cursor: "pointer",
                  padding: "8px 16px",
                  transition: "color 0.2s",
                }}
                onMouseEnter={(e) => e.target.style.color = "rgba(255,255,255,0.7)"}
                onMouseLeave={(e) => e.target.style.color = "rgba(255,255,255,0.5)"}
              >
                ← BACK
              </button>

              <div style={{
                marginTop: 20,
                fontFamily: "monospace",
                fontSize: 9,
                letterSpacing: 3,
                color: "rgba(255,255,255,0.5)",
              }}>
                NEURAL ARCADE v1.0
              </div>
              <div className="mt-2 text-center text-xs" style={{ color: "hsl(var(--muted-foreground) / 0.5)" }}>
                              secret unlocked ✦ type <span style={{ color: "hsl(var(--primary) / 0.6)", fontFamily: "monospace" }}>music</span> for more
                            </div>
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}